import { useEffect, useState } from 'react';
import { authAPI, validatorGroupsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/common/Navbar';
import { toast } from 'react-toastify';

const AdminValidatorGroups = () => {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [supervisorId, setSupervisorId] = useState('');
  const [supervisedId, setSupervisedId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.is_superuser) return;
    Promise.all([loadGroups(), loadUsers()]).finally(() => setLoading(false));
  }, [user]);

  const loadGroups = async () => {
    try {
      const res = await validatorGroupsAPI.list();
      setGroups(res.data || []);
    } catch (e) {
      console.error('Failed to load groups', e);
      toast.error('Error al cargar grupos');
    }
  };

  const loadUsers = async () => {
    try {
      const res = await authAPI.getAllUsers();
      setUsers((res.data || []).filter(u => u.is_active));
    } catch (e) {
      console.error('Failed to load users', e);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!supervisorId || !supervisedId) {
      toast.warning('Seleccioná supervisor y alumno');
      return;
    }
    if (supervisorId === supervisedId) {
      toast.warning('Un usuario no puede supervisarse a sí mismo');
      return;
    }
    setSaving(true);
    try {
      await validatorGroupsAPI.create({ supervisor_id: Number(supervisorId), supervised_user_id: Number(supervisedId) });
      toast.success('Asignación creada');
      setSupervisorId('');
      setSupervisedId('');
      await loadGroups();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Error al crear asignación';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await validatorGroupsAPI.delete(id);
      toast.success('Asignación eliminada');
      await loadGroups();
    } catch {
      toast.error('Error al eliminar asignación');
    }
  };

  if (!user?.is_superuser) {
    return (
      <div>
        <Navbar />
        <div className="p-6">No autorizado</div>
      </div>
    );
  }

  // Group assignments by supervisor for cleaner display
  const bySupervisor = groups.reduce((acc, g) => {
    const key = `${g.supervisor_id}__${g.supervisor_username}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(g);
    return acc;
  }, {});

  return (
    <div>
      <Navbar />
      <div className="max-w-4xl mx-auto p-6">
        <h2 className="text-xl font-semibold mb-2">Grupos de validación (Profe / Alumno)</h2>
        <p className="text-sm text-gray-600 mb-6">
          Cuando un supervisor entra a validar, solo verá las sugerencias hechas por sus alumnos asignados.
        </p>

        {/* Add assignment form */}
        <form onSubmit={handleAdd} className="bg-gray-50 border rounded p-4 mb-8 space-y-3">
          <h3 className="font-medium">Nueva asignación</h3>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Supervisor (Profe)</label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={supervisorId}
                onChange={e => setSupervisorId(e.target.value)}
                required
              >
                <option value="">Seleccionar usuario...</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.username} ({u.email})</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Supervisado (Alumno)</label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={supervisedId}
                onChange={e => setSupervisedId(e.target.value)}
                required
              >
                <option value="">Seleccionar usuario...</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.username} ({u.email})</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Guardando...' : 'Agregar asignación'}
            </button>
          </div>
        </form>

        {/* Existing assignments */}
        {loading ? (
          <div>Cargando...</div>
        ) : groups.length === 0 ? (
          <div className="text-gray-500 text-sm">No hay asignaciones configuradas.</div>
        ) : (
          <div className="space-y-6">
            {Object.entries(bySupervisor).map(([key, entries]) => {
              const supervisorUsername = entries[0].supervisor_username;
              return (
                <div key={key} className="border rounded overflow-hidden">
                  <div className="bg-blue-50 px-4 py-2 font-medium text-sm text-blue-800">
                    Supervisor: {supervisorUsername}
                  </div>
                  <ul className="divide-y">
                    {entries.map(g => (
                      <li key={g.id} className="flex items-center justify-between px-4 py-2">
                        <div className="text-sm text-gray-700">
                          <span className="font-medium">{g.supervised_username}</span>
                          <span className="ml-2 text-xs text-gray-400">
                            desde {new Date(g.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <button
                          onClick={() => handleDelete(g.id)}
                          className="text-red-500 hover:text-red-700 text-xs font-medium"
                        >
                          Eliminar
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminValidatorGroups;
